import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle, Shield, Award, Zap, ThumbsUp, ChevronDown, ChevronUp, Star, ExternalLink, ClipboardCheck, Home, Wrench } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/C8s7vSJPZ2sHXxe16";

const FAQS = [
  {
    question: "Quel budget pour une pompe à chaleur à Clairac ?",
    answer: "À Clairac, une pompe à chaleur coûte entre 8 000€ et 15 000€ avant aides, selon le modèle retenu et la surface de votre habitation. Les dispositifs MaPrimeRénov' et CEE (au maximum depuis mars 2025) permettent de ramener votre investissement à quelques centaines d'euros. François Delattre réalise votre étude gratuite sur place."
  },
  {
    question: "Quelles aides pour poser une PAC à Clairac ?",
    answer: "Les Clairacais bénéficient de MaPrimeRénov' (jusqu'à 5 000€), des primes CEE (jusqu'à 6 550€ depuis mars 2025) et de la TVA réduite à 5,5%. François Delattre détient la certification RGE QualiPAC, indispensable pour accéder à ces aides, et monte gratuitement l'ensemble de vos dossiers."
  },
  {
    question: "En combien de temps est posée une PAC à Clairac ?",
    answer: "L'installation prend 1 à 2 jours en règle générale. François est basé à Pinel-Hauterive, à environ 30 minutes de Clairac, et organise votre chantier pour que tout soit opérationnel le plus vite possible."
  },
  {
    question: "Quelle pompe à chaleur pour une maison ancienne à Clairac ?",
    answer: "Clairac, ancienne cité au bord du Lot, possède un patrimoine bâti riche : maisons de caractère en pierre, longères et pavillons plus récents. Beaucoup sont encore chauffés au fioul ou avec des convecteurs électriques vieillissants. La PAC air/eau est le choix le plus fréquent : elle se branche sur vos radiateurs sans travaux lourds et gère aussi l'eau chaude. Pour les habitations sans réseau hydraulique, la PAC air/air réversible combine chauffage et climatisation. François vient gratuitement définir la solution la plus adaptée."
  },
  {
    question: "François Delattre intervient-il à Clairac ?",
    answer: "Oui, Clairac fait partie de son secteur d'intervention. À 30 minutes de Pinel-Hauterive, François se déplace régulièrement dans le coin et couvre aussi Tonneins, Aiguillon, Damazan, Villeneuve-sur-Lot et tout le Lot-et-Garonne."
  },
  {
    question: "Quel reste à charge pour une PAC à Clairac après les aides ?",
    answer: "Pour une maison de 100m² à Clairac, une PAC air/eau revient à environ 12 500€ posée. Après MaPrimeRénov' (5 000€) et la prime CEE (jusqu'à 6 550€), il ne reste qu'environ 950€ à votre charge. François monte gratuitement la totalité de vos dossiers d'aides."
  }
];

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
      ))}
    </div>
  );
}

function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

function ReviewCard({ review }) {
  return (
    <Card className="bg-white border border-gray-100 rounded-xl shadow-sm h-full">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center gap-3 mb-3">
          {review.photo ? (
            <img src={review.photo} alt={review.name} className="w-9 h-9 rounded-full object-cover" width={36} height={36} />
          ) : (
            <div className="w-9 h-9 bg-[#6AB023] rounded-full flex items-center justify-center text-white font-bold text-xs">{getInitials(review.name)}</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
            <p className="text-xs text-gray-400">{review.date}</p>
          </div>
          <GoogleLogo />
        </div>
        <StarRating rating={review.rating} />
        <p className="text-sm text-gray-700 leading-relaxed mt-3">{review.text}</p>
      </CardContent>
    </Card>
  );
}

function FAQItem({ faq, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors">
        <span className="font-semibold text-gray-900 text-sm md:text-base">{faq.question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#6AB023] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#6AB023] flex-shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 bg-white"><p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p></div>}
    </div>
  );
}

function MiniReviewBadge({ rating, total }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <GoogleLogo />
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
        ))}
      </div>
      <span className="font-semibold text-gray-900">{rating.toFixed(1)}</span>
      <span className="text-gray-400">({total} avis)</span>
    </div>
  );
}

function ContactForm({ rating, total }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("PAC Air/Eau");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !phone.trim()) { setError("Veuillez remplir votre nom et téléphone."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { name: name.trim(), phone: phone.trim(), service, message: message.trim() });
      setSuccess(true);
      setName(""); setPhone(""); setService("PAC Air/Eau"); setMessage("");
    } catch { setError("Une erreur est survenue. Veuillez nous appeler directement au 06 28 51 49 15."); }
    finally { setLoading(false); }
  };

  if (success) return (
    <div className="flex flex-col items-center text-center py-8">
      <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4"><CheckCircle className="w-8 h-8 text-[#6AB023]" /></div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
      <p className="text-gray-600 text-sm">François vous rappelle sous 24h pour organiser sa venue à Clairac.</p>
      <Button onClick={() => setSuccess(false)} variant="outline" className="mt-6 border-[#6AB023] text-[#6AB023] hover:bg-green-50 rounded-full">Nouvelle demande</Button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-1">Devis gratuit — sans engagement</h3>
        <p className="text-sm text-gray-500">François vous rappelle sous 24h.</p>
      </div>

      {total > 0 && (
        <div className="review-badge-pulse bg-green-50/60 rounded-lg p-3 flex items-center justify-center">
          <MiniReviewBadge rating={rating} total={total} />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre nom <span className="text-red-400">*</span></label>
        <Input type="text" placeholder="Jean Dupont" value={name} onChange={(e) => setName(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre téléphone <span className="text-red-400">*</span></label>
        <Input type="tel" placeholder="06 12 34 56 78" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Type de projet</label>
        <select value={service} onChange={(e) => setService(e.target.value)} className="w-full h-12 border border-gray-300 rounded-lg px-3 text-sm text-gray-700 bg-white contact-input-focus focus:outline-none">
          <option value="PAC Air/Eau">Installation PAC Air/Eau</option>
          <option value="PAC Air/Air">Installation PAC Air/Air</option>
          <option value="Chauffe-eau thermodynamique">Chauffe-eau thermodynamique</option>
          <option value="Climatisation réversible">Climatisation réversible</option>
          <option value="Remplacement chaudière fioul">Remplacement chaudière fioul</option>
          <option value="Autre">Autre</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Décrivez votre projet <span className="text-xs text-gray-400 font-normal">(optionnel)</span></label>
        <textarea placeholder="Ex: Longère en pierre, remplacement chauffage fioul..." value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white contact-input-focus focus:outline-none resize-none" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button type="submit" disabled={loading} className="btn-shine w-full bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full h-12 text-base gap-2">
        {loading ? <span className="flex items-center gap-2"><svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Envoi en cours...</span> : <><Send className="w-5 h-5" />Recevoir mon devis gratuit</>}
      </Button>
      <p className="text-xs text-gray-400 text-center">Sans engagement · Vos données restent confidentielles</p>
    </form>
  );
}

function StickyMobileCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (!visible) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] p-3 md:hidden">
      <a href="#devis" className="block">
        <Button className="btn-shine w-full bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full h-12 gap-2 text-base">
          <Send className="w-5 h-5" />Recevoir mon devis gratuit
        </Button>
      </a>
    </div>
  );
}

export default function InstallationPACClairac() {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(res => {
      setReviews(res.data.reviews);
      setRating(res.data.rating);
      setTotal(res.data.total);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("revealed");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
      );
      const elements = document.querySelectorAll(".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale");
      elements.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    }, 100);
    return () => clearTimeout(timer);
  }, [reviews]);

  useEffect(() => {
  document.title = "Installation Pompe à Chaleur Clairac | Alter Energies";
  const meta = document.querySelector('meta[name="description"]');
  const content = "Pompe à chaleur à Clairac par Alter Energies. François Delattre, chauffagiste RGE QualiPAC à 30 min, installe votre PAC air/eau ou air/air. Devis gratuit, aides gérées.";
  if (meta) { meta.setAttribute("content", content); } else { const m = document.createElement("meta"); m.name = "description"; m.content = content; document.head.appendChild(m); }
}, []);

  return (
    <div className="min-h-screen bg-white">

      <StickyMobileCTA />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-green-50 to-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="hero-badge hero-badge-shimmer inline-flex items-center gap-2 bg-green-100 text-[#6AB023] rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                <MapPin className="w-4 h-4" />Clairac · Lot-et-Garonne (47)
              </div>
              <h1 className="hero-title text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
                Installation Pompe à Chaleur à <span className="text-[#6AB023]">Clairac</span>
              </h1>
              <p className="hero-subtitle text-lg text-gray-600 mb-8 leading-relaxed">
                Vous êtes à Clairac et souhaitez abandonner le fioul pour un chauffage moderne et économique ? François Delattre, artisan chauffagiste certifié RGE QualiPAC avec 20 ans d'expérience, se déplace chez vous pour installer votre PAC air/eau ou air/air. Devis offert, aides MaPrimeRénov' et CEE entièrement gérées.
              </p>
              <div className="hero-cta flex flex-col sm:flex-row gap-4 mb-8">
                <a href="tel:0628514915"><Button className="bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full px-8 h-12 gap-2 text-base"><Phone className="w-5 h-5" />06 28 51 49 15</Button></a>
                <a href="#devis"><Button variant="outline" className="border-[#6AB023] text-[#6AB023] hover:bg-green-50 font-bold rounded-full px-8 h-12 text-base">Devis gratuit →</Button></a>
              </div>
              <div className="hero-trust flex flex-wrap gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#6AB023]" />Certifié RGE QualiPAC</span>
                <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-[#6AB023]" />20 ans d'expérience</span>
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#6AB023]" />À 30 min de Clairac</span>
              </div>
            </div>
            <div className="hero-image relative">
              <div className="bg-green-50 rounded-2xl p-2">
                <img src="https://customer-assets.emergentagent.com/job_chauffage-pac/artifacts/d8avbt7b_IMG_8167.jpeg" alt="Installation pompe à chaleur à Clairac - Alter Energies chauffagiste RGE Lot-et-Garonne" className="w-full rounded-xl object-cover" width={600} height={400} />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"><ThumbsUp className="w-5 h-5 text-[#6AB023]" /></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">500+ installations</p>
                  <p className="text-xs text-gray-500">en Lot-et-Garonne</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parcours client */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Votre projet PAC à Clairac, <span className="text-[#6AB023]">tout compris</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">François gère votre projet de bout en bout. Un seul interlocuteur, aucune sous-traitance.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: "1", icon: Phone, title: "Prenez contact", desc: "Un appel ou un formulaire suffit. François vous recontacte sous 24h pour échanger sur votre projet à Clairac et ses alentours." },
              { step: "2", icon: ClipboardCheck, title: "Visite gratuite chez vous", desc: "François vient à Clairac inspecter votre chauffage actuel et vous présenter un devis complet, toutes aides déjà déduites." },
              { step: "3", icon: Wrench, title: "Pose en 1 à 2 jours", desc: "Installation certifiée RGE, soignée et efficace. François gère aussi vos dossiers MaPrimeRénov' et CEE gratuitement." },
            ].map((item, idx) => (
              <div key={item.step} className={`relative text-center scroll-reveal-scale stagger-${idx + 1}`}>
                <div className="w-14 h-14 bg-[#6AB023] rounded-full flex items-center justify-center mx-auto mb-5 text-white font-extrabold text-xl shadow-md">{item.step}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 scroll-reveal">
            <a href="#devis"><Button className="btn-shine bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full px-8 h-12 text-base gap-2"><Send className="w-5 h-5" />Démarrer mon projet</Button></a>
          </div>
        </div>
      </section>

      {/* Aides financières */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Aides record pour <span className="text-[#6AB023]">votre PAC à Clairac</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Les aides atteignent des niveaux historiques. François, certifié RGE, vous ouvre l'accès à chaque dispositif.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { title: "MaPrimeRénov'", amount: "jusqu'à 5 000€", desc: "Aide de l'État pour en finir avec le fioul" },
              { title: "CEE", amount: "jusqu'à 6 550€", desc: "Primes énergie au plus haut depuis mars 2025" },
              { title: "TVA réduite", amount: "5,5%", desc: "Taux préférentiel sur la totalité du chantier" },
            ].map((aide, idx) => (
              <Card key={aide.title} className={`scroll-reveal-scale stagger-${idx + 1} border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow`}>
                <CardContent className="p-6 text-center">
                  <p className="text-2xl font-extrabold text-[#6AB023] mb-1">{aide.amount}</p>
                  <p className="font-bold text-gray-900 mb-2">{aide.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{aide.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="max-w-3xl mx-auto scroll-reveal">
            <Card className="border-2 border-[#6AB023]/20 rounded-xl bg-white shadow-sm">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"><Home className="w-5 h-5 text-[#6AB023]" /></div>
                  <h3 className="text-lg font-bold text-gray-900">Exemple : maison de caractère 100m² à Clairac</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm"><span className="text-gray-600">PAC Air/Eau complète</span><span className="font-semibold text-gray-900">12 500€</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">MaPrimeRénov'</span><span className="font-semibold text-[#6AB023]">- 5 000€</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Prime CEE</span><span className="font-semibold text-[#6AB023]">- 6 550€</span></div>
                    <div className="border-t border-gray-200 pt-3 flex justify-between"><span className="font-bold text-gray-900">Vous déboursez</span><span className="font-extrabold text-[#6AB023] text-xl">950€*</span></div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 flex flex-col justify-center">
                    <p className="text-sm text-gray-700 leading-relaxed"><span className="font-bold text-[#6AB023]">92% financés par les aides.</span> François gère toute l'administratif à votre place.</p>
                    <p className="text-xs text-gray-400 mt-3">*Montant indicatif selon revenus du foyer.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services PAC */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Deux <span className="text-[#6AB023]">solutions PAC</span> pour votre maison à Clairac</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">François vous conseille la pompe à chaleur la mieux adaptée à votre habitation et à votre installation existante.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: "PAC Air/Eau", desc: "La solution privilégiée pour les maisons de Clairac encore chauffées au fioul. Elle se raccorde à vos radiateurs existants sans modification majeure et assure aussi la production d'eau chaude sanitaire.", points: ["Compatible radiateurs et plancher chauffant existants", "Eau chaude sanitaire intégrée", "Économies de chauffage de 50 à 70%"] },
              { title: "PAC Air/Air", desc: "Chauffage et climatisation réversible en un seul appareil. Un choix judicieux à Clairac pour les maisons sans réseau de radiateurs, avec un confort garanti été comme hiver dans la vallée du Lot.", points: ["Double fonction : chaud l'hiver, frais l'été", "Installation rapide en une journée", "Régulation indépendante pièce par pièce"] },
            ].map((service, idx) => (
              <Card key={service.title} className={`scroll-reveal-scale stagger-${idx + 1} service-card border border-gray-100 rounded-xl shadow-sm`}>
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Installation {service.title} à Clairac</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{service.desc}</p>
                  <ul className="space-y-2">
                    {service.points.map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-gray-700"><span className="text-[#6AB023] font-bold mt-0.5">→</span>{point}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi nous */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Pourquoi choisir <span className="text-[#6AB023]">Alter Energies</span> à Clairac ?</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "RGE QualiPAC", desc: "La certification qui débloque toutes les aides pour vous." },
              { icon: Award, title: "500+ installations", desc: "20 ans de métier dans le Lot-et-Garonne." },
              { icon: MapPin, title: "30 min de Clairac", desc: "François intervient régulièrement dans le secteur." },
              { icon: ThumbsUp, title: "5 étoiles Google", desc: "Un travail de qualité reconnu par tous nos clients." },
            ].map((item, idx) => (
              <Card key={item.title} className={`scroll-reveal-scale stagger-${idx + 1} service-card border border-gray-100 rounded-xl shadow-sm text-center`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><item.icon className="w-6 h-6 text-[#6AB023]" /></div>
                  <p className="font-bold text-gray-900 mb-2">{item.title}</p>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Avis Google */}
      {reviews.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="text-center mb-10 scroll-reveal">
              <div className="inline-flex items-center gap-2 mb-4"><GoogleLogo /><span className="text-sm font-medium text-gray-500">Avis Google vérifiés</span></div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Satisfaction client dans tout le <span className="text-[#6AB023]">Lot-et-Garonne</span></h2>
              <div className="flex items-center justify-center gap-3 mt-4">
                <span className="text-4xl font-bold text-gray-900">{rating.toFixed(1)}</span>
                <div><StarRating rating={Math.round(rating)} /><p className="text-xs text-gray-500 mt-1">{total} avis sur Google</p></div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {reviews.slice(0, 3).map((review, idx) => (
                <div key={review.name} className={`scroll-reveal-scale stagger-${idx + 1}`}><ReviewCard review={review} /></div>
              ))}
            </div>
            <div className="text-center scroll-reveal">
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-[#6AB023] text-[#6AB023] hover:bg-green-50 rounded-full px-8 h-11 font-semibold gap-2"><GoogleLogo />Voir nos {total > 0 ? total : ""} avis Google<ExternalLink className="w-4 h-4" /></Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Formulaire devis */}
      <section id="devis" className="py-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Votre <span className="text-[#6AB023]">devis gratuit</span> à Clairac</h2>
            <p className="text-gray-600">Pompe à chaleur à Clairac — Gratuit et sans engagement</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <Card className="scroll-reveal-left bg-white border border-gray-200 rounded-xl shadow-lg">
              <CardContent className="p-6 md:p-8"><ContactForm rating={rating} total={total} /></CardContent>
            </Card>
            <div className="flex flex-col gap-5 justify-center scroll-reveal-right">
              <a href="tel:0628514915" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors"><Phone className="w-5 h-5 text-[#6AB023] group-hover:text-white transition-colors" /></div>
                <div><p className="font-bold text-gray-900">06 28 51 49 15</p><p className="text-xs text-gray-500">Appel gratuit · Du lundi au samedi</p></div>
              </a>
              <a href="mailto:frs.delattre@gmail.com" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors"><Mail className="w-5 h-5 text-[#6AB023] group-hover:text-white transition-colors" /></div>
                <div><p className="font-bold text-gray-900">frs.delattre@gmail.com</p><p className="text-xs text-gray-500">Réponse sous 24h</p></div>
              </a>
              <div className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center"><MapPin className="w-5 h-5 text-[#6AB023]" /></div>
                <div><p className="font-bold text-gray-900">À 30 min de Clairac</p><p className="text-xs text-gray-500">1899 Côte de Pinel Garrigou · 47380 Pinel-Hauterive</p></div>
              </div>

              <div className="bg-green-50/60 rounded-xl p-5 space-y-3 mt-2">
                <p className="text-sm font-semibold text-gray-900">Ce que François fait pour vous :</p>
                <div className="space-y-2">
                  {[
                    "Déplacement et diagnostic gratuits à Clairac",
                    "Devis transparent avec toutes les aides intégrées",
                    "Montage complet de vos dossiers MaPrimeRénov' et CEE",
                    "Installation certifiée RGE QualiPAC",
                  ].map((item) => (
                    <p key={item} className="flex items-start gap-2 text-sm text-gray-700"><CheckCircle className="w-4 h-4 text-[#6AB023] flex-shrink-0 mt-0.5" />{item}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Maillage interne */}
<section className="py-12 bg-[#F9FAFB]">
  <div className="container mx-auto px-4 md:px-6 max-w-4xl">
    <div className="scroll-reveal text-center mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Installation PAC dans le <span className="text-[#6AB023]">Lot-et-Garonne</span></h2>
      <p className="text-sm text-gray-600">François intervient dans tout le département. Découvrez nos interventions près de chez vous.</p>
    </div>
    <div className="flex flex-wrap justify-center gap-3 scroll-reveal">
      <a href="/installation-pompe-a-chaleur-lot-et-garonne" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#6AB023]/30 rounded-full text-sm font-medium text-[#6AB023] hover:bg-green-50 transition-colors">Lot-et-Garonne (47)</a>
      <a href="/installation-pompe-a-chaleur-tonneins" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-[#6AB023] hover:text-[#6AB023] transition-colors">Tonneins</a>
      <a href="/installation-pompe-a-chaleur-aiguillon" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-[#6AB023] hover:text-[#6AB023] transition-colors">Aiguillon</a>
      <a href="/installation-pompe-a-chaleur-damazan" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-[#6AB023] hover:text-[#6AB023] transition-colors">Damazan</a>
    </div>
  </div>
</section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions fréquentes — <span className="text-[#6AB023]">PAC à Clairac</span></h2>
          </div>
          <div className="space-y-3 scroll-reveal">
            {FAQS.map((faq, index) => <FAQItem key={faq.question} faq={faq} defaultOpen={index === 0} />)}
          </div>
        </div>
      </section>

      <div className="h-16 md:hidden" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Installation pompe à chaleur Clairac",
        "description": "Installation de pompe à chaleur air/eau et air/air à Clairac par Alter Energies, chauffagiste certifié RGE en Lot-et-Garonne (47). Remplacement chaudière fioul, devis gratuit, éligible MaPrimeRénov'.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Alter Energies - François Delattre",
          "telephone": "+33628514915",
          "email": "frs.delattre@gmail.com",
          "address": { "@type": "PostalAddress", "streetAddress": "1899 Côte de Pinel Garrigou", "addressLocality": "Pinel-Hauterive", "postalCode": "47380", "addressRegion": "Lot-et-Garonne", "addressCountry": "FR" },
          "geo": { "@type": "GeoCoordinates", "latitude": "44.3833", "longitude": "0.7667" },
          "url": "https://alterenergies-47.com",
          "priceRange": "€€",
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "21", "bestRating": "5" }
        },
        "areaServed": [
          { "@type": "City", "name": "Clairac" },
          { "@type": "City", "name": "Tonneins" },
          { "@type": "City", "name": "Aiguillon" },
          { "@type": "City", "name": "Damazan" },
          { "@type": "City", "name": "Villeneuve-sur-Lot" },
          { "@type": "AdministrativeArea", "name": "Lot-et-Garonne" }
        ],
        "serviceType": ["Installation pompe à chaleur", "Remplacement chaudière fioul", "Installation PAC air/eau", "Installation PAC air/air", "Chauffagiste RGE"]
      })}} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
        }))
      })}} />

    </div>
  );
}
